import { Injectable } from '@angular/core';
import { PasswordValidationStatus } from 'firebase/auth';
import { ZxcvbnFactory } from '@zxcvbn-ts/core';
import * as common from '@zxcvbn-ts/language-common';
import * as english from '@zxcvbn-ts/language-en';
import { PasswordResetService } from './password-reset.service';

export interface PasswordStrengthState {
  readonly checked: boolean;
  readonly isStrong: boolean;
  readonly preThreshold: boolean;
  readonly minCheckLength: number;
  readonly policy: PasswordValidationStatus | null;
  readonly guessability: boolean | null;
  readonly compromised: boolean | null;
}

export function createInitialPasswordStrengthState(minCheckLength = 12): PasswordStrengthState {
  return {
    checked: false,
    isStrong: false,
    preThreshold: true,
    minCheckLength,
    policy: null,
    guessability: null,
    compromised: null,
  };
}

@Injectable({ providedIn: 'root' })
export class FirebasePasswordPolicyService {
  readonly debounceMs = 400;
  readonly minGuessabilityScore = 4;
  readonly maxPasswordLengthForScoring = 100;
  private minCheckLength = 12;
  private initialization?: Promise<void>;
  private readonly cache = new Map<string, PasswordStrengthState>();
  private readonly zxcvbn = new ZxcvbnFactory({
    dictionary: { ...common.dictionary, ...english.dictionary },
    graphs: common.adjacencyGraphs,
    translations: english.translations,
  });

  constructor(private readonly reset: PasswordResetService) {}

  async getInitialState(): Promise<PasswordStrengthState> {
    await this.initializePolicy();
    return createInitialPasswordStrengthState(this.minCheckLength);
  }

  clearCache(): void {
    this.cache.clear();
  }

  async evaluatePassword(
    password: string,
    revalidateAllCriteria = false,
  ): Promise<PasswordStrengthState> {
    await this.initializePolicy();
    if (!password || password.length < this.minCheckLength) {
      return createInitialPasswordStrengthState(this.minCheckLength);
    }
    if (!revalidateAllCriteria && this.cache.has(password)) return this.cache.get(password);

    const policy = await this.reset.validate(password);
    this.updateMinLength(policy);
    const guessability =
      this.zxcvbn.check(password.slice(0, this.maxPasswordLengthForScoring)).score >=
      this.minGuessabilityScore;
    let compromised: boolean | null = null;
    if (revalidateAllCriteria && policy.isValid && guessability) {
      compromised = await this.isCompromisedPassword(password);
    }
    const state: PasswordStrengthState = {
      checked: true,
      isStrong: policy.isValid && guessability && compromised !== true,
      preThreshold: false,
      minCheckLength: this.minCheckLength,
      policy,
      guessability,
      compromised,
    };
    if (!revalidateAllCriteria) {
      if (this.cache.size >= 100) this.cache.delete(this.cache.keys().next().value);
      this.cache.set(password, state);
    }
    return state;
  }

  getPasswordValidationErrorMessage(error: unknown): string {
    switch ((error as { code?: string })?.code) {
      case 'auth/weak-password':
      case 'auth/password-does-not-meet-requirements':
        return 'Het wachtwoord voldoet niet aan de vereisten. Kies een sterker wachtwoord.';
      case 'auth/too-many-requests':
        return 'Te veel pogingen om het wachtwoord te controleren. Probeer het later opnieuw.';
      case 'auth/network-request-failed':
        return 'Het controleren van het wachtwoord is mislukt door een netwerkfout. Probeer het opnieuw.';
      default:
        return 'Het wachtwoord kon niet worden gecontroleerd. Probeer het opnieuw.';
    }
  }

  private async initializePolicy(): Promise<void> {
    this.initialization ??= this.reset
      .validate('PolicyProbeAa1!PolicyProbe')
      .then((policy) => this.updateMinLength(policy))
      .catch(() => undefined);
    await this.initialization;
  }

  private updateMinLength(policy: PasswordValidationStatus): void {
    const minimum = policy.passwordPolicy?.customStrengthOptions?.minPasswordLength;
    if (Number.isFinite(minimum) && minimum > 0) this.minCheckLength = minimum;
  }

  private async isCompromisedPassword(password: string): Promise<boolean> {
    try {
      const digest = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(password));
      const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();
      const response = await fetch(`https://api.pwnedpasswords.com/range/${hash.slice(0, 5)}`, {
        headers: { 'Add-Padding': 'true' },
        signal: AbortSignal.timeout(8000),
        referrerPolicy: 'no-referrer',
      });
      if (response.status === 429) throw { code: 'auth/too-many-requests' };
      if (!response.ok) throw { code: 'auth/network-request-failed' };
      const text = await response.text();
      let hasValidEntry = false;
      let compromised = false;
      for (const line of text.split('\n')) {
        const match = /^([A-F0-9]{35}):(\d+)$/i.exec(line.trim());
        if (!match) continue;
        hasValidEntry = true;
        if (match[1].toUpperCase() === hash.slice(5) && Number(match[2]) > 0) compromised = true;
      }
      if (!hasValidEntry) throw { code: 'auth/network-request-failed' };
      return compromised;
    } catch (error) {
      if ((error as { code?: string })?.code === 'auth/too-many-requests') throw error;
      throw { code: 'auth/network-request-failed' };
    }
  }
}
